// src/modules/app-views/invoices/pages/Init/modals/EmitInvoiceNfceModal/index.jsx
// *******************************************************************************
// EMIT INVOICE NFC-e MODAL
// *******************************************************************************
// Modal for emitting NFC-e with optional CPF input.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import invoicesService from 'modules/app-views/invoices/service';
import appService from 'services/appService';
import { returnOnlyNumbers } from 'utils/helpers';
import { isValidCpf } from 'utils/validation';
import createEmitInvoiceStep from './Step1';

// *******************************************************************************
// THEME WRAPPER (required for modals with createRoot)
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * EmitInvoiceModal - Modal for NFC-e emission with optional CPF input
 *
 * @param {boolean} show - Whether modal is visible
 * @param {Function} setShow - Function to set modal visibility
 * @param {Function} afterClose - Callback after modal closes
 * @param {Object} invoiceData - Invoice data object
 * @param {Function} onSuccess - Callback when emission succeeds (legacy mode)
 * @param {Function} onCpfCollected - If provided, modal only collects CPF and calls this callback
 *                                    instead of doing the emission (new mode for Document page)
 */
const EmitInvoiceModal = ({ show, setShow, afterClose, invoiceData, onSuccess, onError, onCpfCollected }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(() => [createEmitInvoiceStep()], []);

  // Determine if we're in "collect CPF only" mode
  const collectCpfOnly = typeof onCpfCollected === 'function';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    // Get CPF value from form (SimpleCpf stores directly in 'cpf', not 'cpf.value')
    const cpfValue = form.getFieldValue('cpf');
    const cleanCpf = returnOnlyNumbers(cpfValue);

    // If CPF is entered but invalid, show error and don't proceed
    if (cleanCpf && !isValidCpf(cleanCpf)) {
      appService.message({
        flag: 'error',
        text: 'cpf_invalid',
      });
      return false;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // MODE 1: Collect CPF only - return CPF via callback, don't emit
    // ───────────────────────────────────────────────────────────────────────────
    if (collectCpfOnly) {
      onCpfCollected(cleanCpf || null);
      return true; // Close modal
    }

    // ───────────────────────────────────────────────────────────────────────────
    // MODE 2: Legacy mode - emit directly (for Init page)
    // Close modal immediately and emit in background
    // ───────────────────────────────────────────────────────────────────────────
    const invoiceId = invoiceData?._id;
    const invoiceNumber = invoiceData?.documentNumber || invoiceData?._id?.slice(-6);
    const documentType = invoiceData?.documentType?.toUpperCase() || 'NFCE';

    // Build emit payload
    const emitPayload = {
      cpf: cleanCpf || null, // Send CPF if provided, null otherwise
    };

    // Show loading message (will be closed by background process)
    appService.message({
      flag: 'loading',
      text: 'emitting_invoice',
      key: `emit-${invoiceId}`, // Unique key for this emission
      duration: 0,
    });

    // Emit in background (don't await)
    emitInvoiceInBackground(invoiceId, documentType, emitPayload, invoiceNumber, onSuccess, onError);

    // Close modal immediately so user can continue
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<CloudUploadOutlined />}
      title="emit_nfce_title"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={400}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (creates modal in React root)
// *******************************************************************************
/**
 * Show the EmitInvoiceNfceModal
 *
 * @param {Object} options
 * @param {Object} options.invoiceData - Invoice data object
 * @param {Function} options.onSuccess - Callback when emission succeeds (legacy mode)
 * @param {Function} options.onError - Callback when emission fails (to update drawer and refresh)
 * @param {Function} options.onCpfCollected - If provided, modal only collects CPF and calls this callback
 * @param {Function} options.onClose - Callback after modal closes
 */
const showEmitInvoiceNfceModal = ({ invoiceData, onSuccess, onError, onCpfCollected, onClose } = {}) => {
  // Create container
  const container = document.createElement('div');
  container.id = 'emit-invoice-modal-container';
  document.body.appendChild(container);

  // Create React root
  const root = createRoot(container);

  // Track modal state
  let isModalOpen = true;

  // Render function
  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <EmitInvoiceModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          invoiceData={invoiceData}
          onSuccess={onSuccess}
          onError={onError}
          onCpfCollected={onCpfCollected}
        />
      </ThemeWrapper>
    );
  };

  // Close handler
  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  // AfterClose handler
  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      onClose?.();
    }, 0);
  };

  // Initial render
  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showEmitInvoiceNfceModal;
export { EmitInvoiceModal };

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Emit invoice in background - allows user to continue while emission is processing
 * @param {string} invoiceId - Invoice ID
 * @param {string} documentType - Document type (NFE, NFCE)
 * @param {Object} emitPayload - Payload with CPF (optional)
 * @param {string} invoiceNumber - Invoice number for display
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
async function emitInvoiceInBackground(invoiceId, documentType, emitPayload, invoiceNumber, onSuccess, onError) {
  try {
    // Call emit service
    const result = await invoicesService.emit(invoiceId, documentType, emitPayload);

    if (result?.success) {
      // Show subtle success message (notification removed - message is enough)
      appService.message({
        flag: 'success',
        text: 'invoice_emitted_successfully',
        key: `emit-${invoiceId}`, // Replace loading message with success
      });

      // Call success callback (e.g., refresh data)
      onSuccess?.(result);
    } else {
      // Show error from backend (SEFAZ rejection or other emission error)
      const errorMessage = result?.error || result?.message || 'emit_invoice_error';
      appService.notification({
        flag: 'error',
        title: 'emit_invoice_error',
        description: `#${invoiceNumber}: ${errorMessage}`,
        noTranslateDescription: true,
      });

      // Call onError callback to update drawer and refresh list
      onError?.();
    }
  } catch (error) {
    // Close loading message
    appService.messageDestroy(`emit-${invoiceId}`);

    console.error('[EmitInvoiceModal] Emit error:', error);
    appService.notification({
      flag: 'error',
      title: 'emit_invoice_error',
      description: `#${invoiceNumber}: ${error?.message || 'unknown_error'}`,
      noTranslateDescription: true,
    });

    // Call onError callback to update drawer and refresh list
    onError?.();
  }
}
